import { Alert, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { MapAccountsDialog } from './MapAccountsDialog';
import { readPlaidLink } from './plaidLinkStore';
import { useBankConnect } from './useBankConnect';

/**
 * OAuth return (`/connect/plaid/return`). Restores the short-lived link token from
 * sessionStorage and re-opens Link with `receivedRedirectUri`. Never stores a public token. It
 * never starts a repair: a stored repair token resumes the session `link-session` issued when the
 * connection needed one, and ends in `sync`.
 */
export function PlaidReturnPage() {
  const { t } = useTranslation();
  const me = useMe();
  const title = usePageTitle({ title: t('connections.title') });
  const stored = me.tier === 'SUBSCRIBER' ? readPlaidLink() : null;

  // One `PageHeader` for every state, so the `h1` stays put while the resume state changes.
  return (
    <>
      <PageHeader title={title} />
      {stored === null ? (
        <Alert severity="info">
          {t('connections.nothingToResume')}{' '}
          <Link component={RouterLink} to="/me/accounts">
            {t('nav.accounts')}
          </Link>
        </Alert>
      ) : (
        <PlaidResume storedItemId={stored.itemId} token={stored.token} />
      )}
    </>
  );
}

function PlaidResume({
  storedItemId,
  token,
}: {
  storedItemId?: number | undefined;
  token: string;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const notify = useNotify();
  const connect = useBankConnect({
    itemId: storedItemId,
    resumeToken: token,
    receivedRedirectUri: window.location.href,
    onConnected: () => {
      void navigate('/me/accounts');
    },
  });

  return (
    <Stack spacing={2}>
      {connect.mapping === null && !connect.sessionExpired && (
        <Typography variant="body1">{t('connections.resuming')}</Typography>
      )}
      {connect.sessionExpired && (
        <Alert severity="warning">
          {t('connections.sessionNotFound')}{' '}
          <Link component={RouterLink} to="/me/accounts">
            {t('nav.accounts')}
          </Link>
        </Alert>
      )}
      {connect.unavailable && <Alert severity="warning">{t('connections.unavailable')}</Alert>}
      {connect.errorMessage !== null && (
        <Alert severity="error">
          {connect.errorMessage
            ? t('connections.linkFailed', { message: connect.errorMessage })
            : t('connections.linkFailedGeneric')}
        </Alert>
      )}
      {connect.mapping !== null && (
        <MapAccountsDialog
          mapping={connect.mapping}
          operatingCurrency={me.user.currency}
          onClose={() => {
            connect.dismissMapping();
            void navigate('/me/accounts');
          }}
          onConnected={() => {
            notify(t('connections.connected'));
            connect.dismissMapping();
            void navigate('/me/accounts');
          }}
          onSessionExpired={connect.expireSession}
        />
      )}
    </Stack>
  );
}
